import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';
import { TravellerFieldEnum, useGetTravellerProfilesQuery } from '@websky/graphql';
import { getCustomer, getTravellers } from '../../../../store/order/selectors';
import { getCurrency } from '../../../../../cache';
import { AdditionalTravellerFieldEnum, InputType } from '../../../../../Passenger/types';
import { concatPassengerNames, useIsMobile } from '../../../../../utils';
import { getInputType } from './Passengers';
import { getAccompanyingFields, subsidyFieldFactory } from './utils';
import { isSubsidyValidForPassenger } from '../../../../store/subsidies/utils';
import { max, min } from 'date-fns';
export function usePassengers({ fields, labels, services, passengersState, isChangePassengers, onChangeFormSubscription, availableSubsidies, isAuthorized }) {
    var _a, _b;
    const travellers = useSelector(getTravellers);
    const orderCustomer = useSelector(getCustomer);
    const nationalities = new Map();
    const form = useForm();
    const { initialValues } = useFormState();
    const isMobile = useIsMobile();
    const travellerProfilesQuery = useGetTravellerProfilesQuery({
        skip: !isAuthorized
    });
    const travellerProfiles = (_b = (_a = travellerProfilesQuery.data) === null || _a === void 0 ? void 0 : _a.TravellerProfiles) !== null && _b !== void 0 ? _b : [];
    useEffect(() => {
        const unsubscribe = form.subscribe(({ values }) => {
            onChangeFormSubscription === null || onChangeFormSubscription === void 0 ? void 0 : onChangeFormSubscription(values);
        }, { values: true });
        return unsubscribe;
    }, [form]);
    const travellersMap = useMemo(() => {
        return new Map(travellers.map(traveller => [traveller.id, traveller]));
    }, [travellers]);
    const [currentCurrency, servicesMap] = useMemo(() => {
        let currency = getCurrency();
        const servicesByIdMap = new Map(services.map(service => {
            var _a;
            if (currency !== ((_a = service.price) === null || _a === void 0 ? void 0 : _a.currency)) {
                currency = service.price.currency;
            }
            return [service.id, service];
        }));
        return [currency, servicesByIdMap];
    }, [services]);
    const [passengerLabelMap, passengerFieldsMap] = useMemo(() => {
        const labelMap = new Map();
        const fieldsMap = new Map();
        const { values } = form.getState();
        fields.forEach((passenger, passengerId) => {
            var _a, _b, _c, _d;
            let lastName;
            let firstName;
            const maxBirthDates = {
                max: null,
                min: null
            };
            const travellerBirthDateField = passenger.values.find(value => value.type === TravellerFieldEnum.BirthDate);
            const maxDate = max(travellerBirthDateField.validationRules.map(rule => new Date(rule.maxDate)));
            const minDate = min(travellerBirthDateField.validationRules.map(rule => new Date(rule.minDate)));
            maxBirthDates.max = maxDate;
            maxBirthDates.min = minDate;
            const docValidations = (_c = (_b = (_a = passenger.values) === null || _a === void 0 ? void 0 : _a.find(v => v.type === TravellerFieldEnum.DocType)) === null || _b === void 0 ? void 0 : _b.validationRules) !== null && _c !== void 0 ? _c : [];
            (_d = passenger.values) === null || _d === void 0 ? void 0 : _d.forEach(({ name, type, validationRules, value, isEditable }) => {
                var _a, _b, _c, _d, _e;
                if (type === TravellerFieldEnum.Nationality && ((_b = (_a = validationRules === null || validationRules === void 0 ? void 0 : validationRules[0]) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.length)) {
                    validationRules[0].options.map(({ value, label }) => {
                        if (!nationalities.get(value)) {
                            nationalities.set(value, label);
                        }
                    });
                }
                if (!lastName && type === TravellerFieldEnum.LastName) {
                    lastName = value;
                }
                if (!firstName && type === TravellerFieldEnum.FirstName) {
                    firstName = value;
                }
                const initialValue = (value !== null && value !== void 0 ? value : (_c = initialValues.passengers[passengerId]) === null || _c === void 0 ? void 0 : _c[name]);
                const currentValue = (_d = values.passengers[passengerId]) === null || _d === void 0 ? void 0 : _d[name];
                let defaultValue = initialValue;
                if (currentValue && (!initialValue || initialValue !== currentValue)) {
                    defaultValue = currentValue;
                }
                const field = {
                    name,
                    readonly: !isEditable,
                    type,
                    input: getInputType(type),
                    placeholder: name,
                    validations: validationRules,
                    defaultValue
                };
                if ([TravellerFieldEnum.FirstName, TravellerFieldEnum.LastName].includes(type)) {
                    const nameField = field;
                    nameField.nationalities = nationalities;
                    nameField.maxBirthDates = maxBirthDates;
                    nameField.travellerProfiles = travellerProfiles.reduce((acc, traveller) => {
                        const filteredDocs = traveller.documents.filter(doc => {
                            var _a, _b, _c, _d;
                            if (!docValidations.length) {
                                return true;
                            }
                            const docType = (_b = (_a = doc.values.find(v => v.type === TravellerFieldEnum.DocType)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : doc.type;
                            const nationality = (_d = (_c = doc.values.find(v => v.type === TravellerFieldEnum.Citizenship)) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : doc.citizenship;
                            return docValidations
                                .filter(rule => !rule.with ||
                                rule.with.some(w => w.type === TravellerFieldEnum.Nationality && w.value === nationality))
                                .some(rule => rule.options.some(option => option.value === docType));
                        });
                        if (filteredDocs.length > 0) {
                            acc.push(Object.assign(Object.assign({}, traveller), { documents: filteredDocs }));
                        }
                        return acc;
                    }, []);
                }
                fieldsMap.set(passengerId, [...((_e = fieldsMap.get(passengerId)) !== null && _e !== void 0 ? _e : []), field]);
            });
            if (passenger.subsidyInfo) {
                const passengerFields = fieldsMap.get(passengerId) || [];
                const availableSubsidiesForPassenger = availableSubsidies[passengerId];
                if (availableSubsidiesForPassenger && availableSubsidiesForPassenger.length > 0) {
                    // Создаем ОДНО поле со всеми субсидиями
                    passengerFields.push(subsidyFieldFactory(availableSubsidiesForPassenger, true));
                }
                fieldsMap.set(passengerId, [...passengerFields]);
            }
            labelMap.set(passengerId, lastName && firstName ? concatPassengerNames({ lastName, firstName }) : labels[passengerId]);
            if (isChangePassengers) {
                fieldsMap.set(passengerId, [
                    ...(fieldsMap.get(passengerId) || []),
                    {
                        name: 'files',
                        readonly: false,
                        type: AdditionalTravellerFieldEnum.ChangeTravellerFile,
                        input: InputType.Text,
                        placeholder: '',
                        validations: [],
                        defaultValue: null
                    }
                ]);
            }
        });
        return [labelMap, fieldsMap];
    }, [labels, fields, isChangePassengers, initialValues, isMobile, travellerProfiles]);
    const fieldsMapWithSubsidies = useSubsidyFieldsChangerByFormValues(passengerFieldsMap, availableSubsidies);
    const servicesPricesMap = useMemo(() => {
        const prices = new Map();
        passengersState.forEach((passengerState, passengerId) => {
            const servicesPrice = { amount: 0, currency: currentCurrency };
            passengerState.services.forEach(selectedService => {
                const service = servicesMap.get(selectedService.serviceId);
                if (service) {
                    servicesPrice.amount +=
                        selectedService.count * selectedService.segmentIds.length * service.price.amount;
                    servicesPrice.currency = service.price.currency;
                }
            });
            if (passengerState.seats) {
                passengerState.seats.forEach(seat => {
                    if (seat.price) {
                        servicesPrice.amount += seat.price.amount;
                        servicesPrice.currency = seat.price.currency;
                    }
                });
            }
            prices.set(passengerId, servicesPrice);
        });
        return prices;
    }, [passengersState, servicesMap, currentCurrency]);
    const accompanyingFields = useMemo(() => getAccompanyingFields(orderCustomer), [orderCustomer]);
    return {
        nationalities,
        travellersMap,
        passengerLabelMap,
        passengerFieldsMap: fieldsMapWithSubsidies,
        servicesPricesMap,
        accompanyingFields
    };
}
/**
 * @param passengerFieldsMap мапа текущих полей
 * @param availableSubsidiesByPassengers доступные субсидии по пассажирам
 *
 * проставляет для полей субсидий disabled true / false в зависимости от значений пассажира
 * добавляет дополнительные поля из выбранной субсидии в форму пассажира
 *
 * @example
 * если не заполнено имя / фамилия субсидия недоступна для выбора
 * неправильно заполнен снилс - субсидия недоступна для выбора (см subsidy.validation)
 * при выборе субсидии добавляются additionalFields из SubsidyOption
 */
export const useSubsidyFieldsChangerByFormValues = (passengerFieldsMap, availableSubsidiesByPassengers) => {
    var _a, _b;
    const { values } = useFormState({ subscription: { values: true } });
    const updatedFieldsMap = new Map();
    for (const [passengerId, subsidies] of Object.entries(availableSubsidiesByPassengers)) {
        const passengerIdNum = parseInt(passengerId, 10);
        const passengerFields = passengerFieldsMap.get(passengerIdNum);
        if (!passengerFields) {
            continue;
        }
        // Получаем выбранную субсидию для этого пассажира
        const selectedSubsidyId = (_b = (_a = values.passengers) === null || _a === void 0 ? void 0 : _a[passengerIdNum]) === null || _b === void 0 ? void 0 : _b.subsidy;
        const updatedFields = [];
        let subsidyFieldIndex = -1;
        // Обрабатываем все поля пассажира
        passengerFields.forEach((field, index) => {
            if (field.type !== AdditionalTravellerFieldEnum.Subsidy) {
                updatedFields.push(field);
                return;
            }
            subsidyFieldIndex = index;
            const subsidyField = field;
            // Обновляем disabled для каждой опции
            const updatedOptions = subsidyField.options.map(option => {
                const subsidy = subsidies.find(s => s.id === option.value);
                if (!subsidy) {
                    return Object.assign(Object.assign({}, option), { disabled: true });
                }
                const isValid = isSubsidyValidForPassenger(values['passengers'][passengerIdNum], subsidy, [
                    TravellerFieldEnum.Snils
                ]);
                return Object.assign(Object.assign({}, option), { disabled: !isValid });
            });
            const updatedValidations = subsidyField.validations ? [...subsidyField.validations] : [];
            if (updatedValidations[0]) {
                updatedValidations[0] = Object.assign(Object.assign({}, updatedValidations[0]), { options: updatedOptions.map(opt => ({
                        label: opt.label,
                        value: opt.value
                    })) });
            }
            updatedFields.push(Object.assign(Object.assign({}, subsidyField), { options: updatedOptions, validations: updatedValidations }));
        });
        // Добавляем дополнительные поля из выбранной субсидии
        if (selectedSubsidyId && subsidyFieldIndex !== -1) {
            const subsidyField = updatedFields[subsidyFieldIndex];
            const selectedOption = subsidyField.options.find(opt => opt.value === selectedSubsidyId);
            if ((selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.additionalFields) && selectedOption.additionalFields.length > 0) {
                // Получаем имена полей, которые уже есть в форме (до поля subsidy)
                const existingFieldNames = new Set(updatedFields.slice(0, subsidyFieldIndex).map(f => f.name));
                // Добавляем только те дополнительные поля, которых еще нет
                const fieldsToAdd = selectedOption.additionalFields.filter(additionalField => !existingFieldNames.has(additionalField.name));
                // Вставляем дополнительные поля сразу после поля subsidy
                updatedFields.splice(subsidyFieldIndex + 1, 0, ...fieldsToAdd);
            }
        }
        updatedFieldsMap.set(passengerIdNum, updatedFields);
    }
    // Копируем пассажиров, у которых нет субсидий
    for (const [passengerId, fields] of passengerFieldsMap.entries()) {
        if (!updatedFieldsMap.has(passengerId)) {
            updatedFieldsMap.set(passengerId, fields);
        }
    }
    return updatedFieldsMap;
};
